import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Calendar, Mail, Phone, User, MapPin, Euro, Clock, MessageSquare, RefreshCw, Download, Search, Trash2, Lock } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { toast } = useToast();
  const [estimations, setEstimations] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  
  // Mot de passe simple (à changer en production)
  const ADMIN_PASSWORD = "umgroupe2025";

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast({
        title: "Connexion réussie",
        description: "Bienvenue dans l'admin UM Groupe",
      });
    } else {
      toast({
        title: "Erreur",
        description: "Mot de passe incorrect",
        variant: "destructive",
      });
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [estRes, callRes, contRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/submissions/estimations`),
        fetch(`${BACKEND_URL}/api/submissions/callbacks`),
        fetch(`${BACKEND_URL}/api/submissions/contacts`),
        fetch(`${BACKEND_URL}/api/submissions/stats`),
      ]);

      const estData = await estRes.json();
      const callData = await callRes.json();
      const contData = await contRes.json();
      const statsData = await statsRes.json();

      setEstimations(estData);
      setCallbacks(callData);
      setContacts(contData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast({
      title: "Actualisé",
      description: "Les données ont été mises à jour",
    });
  };

  const exportToCSV = () => {
    const csvData = [];
    
    // Add estimations
    estimations.forEach(est => {
      csvData.push({
        Type: "Estimation",
        Nom: est.name,
        Email: est.email,
        Téléphone: est.phone,
        Projet: getProjectTypeLabel(est.project_type),
        Surface: est.surface + " m²",
        Budget: getBudgetLabel(est.budget),
        Délai: getTimelineLabel(est.timeline),
        "Code Postal": est.postal_code,
        Date: formatDate(est.created_at)
      });
    });
    
    // Add callbacks
    callbacks.forEach(cb => {
      csvData.push({
        Type: "Rappel",
        Nom: cb.name,
        Email: "",
        Téléphone: cb.phone,
        Message: cb.message || "",
        Date: formatDate(cb.created_at)
      });
    });
    
    // Add contacts
    contacts.forEach(contact => {
      csvData.push({
        Type: "Contact",
        Nom: contact.name,
        Email: contact.email,
        Téléphone: contact.phone || "",
        Message: contact.message,
        Date: formatDate(contact.created_at)
      });
    });

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {});
    const csv = [
      headers.join(","),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header] || "";
          return `"${value.toString().replace(/"/g, '""')}"`;
        }).join(",")
      )
    ].join("\n");

    // Download
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `umgroupe_soumissions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export réussi",
      description: `${csvData.length} soumissions exportées`,
    });
  };

  const filterData = (data, searchFields) => {
    if (!data || !Array.isArray(data)) return [];
    if (!searchTerm) return data;
    return data.filter(item => 
      searchFields.some(field => 
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProjectTypeLabel = (type) => {
    const labels = {
      photovoltaique: "Photovoltaïque",
      climatisation: "Climatisation",
      energie: "Contrats Énergie",
      fibre: "Fibre Optique"
    };
    return labels[type] || type;
  };

  const getBudgetLabel = (budget) => {
    const labels = {
      "0-5k": "0 - 5 000€",
      "5k-10k": "5 000 - 10 000€",
      "10k-20k": "10 000 - 20 000€",
      "20k+": "Plus de 20 000€"
    };
    return labels[budget] || budget;
  };

  const getTimelineLabel = (timeline) => {
    const labels = {
      urgent: "Urgent (< 1 mois)",
      "1-3mois": "1 à 3 mois",
      "3-6mois": "3 à 6 mois",
      "6mois+": "Plus de 6 mois"
    };
    return labels[timeline] || timeline;
  };

  // Login page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Lock className="text-white" size={32} />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">Admin UM Groupe</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mot de passe
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  className="w-full"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  const filteredEstimations = filterData(estimations, ["name", "email", "phone", "postal_code"]);
  const filteredCallbacks = filterData(callbacks, ["name", "phone"]);
  const filteredContacts = filterData(contacts, ["name", "email", "phone"]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard Admin</h1>
            <p className="text-gray-600">Gestion des soumissions - UM Groupe</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className={refreshing ? "animate-spin" : ""} size={18} />
              Actualiser
            </Button>
            <Button
              onClick={exportToCSV}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              disabled={estimations.length === 0 && callbacks.length === 0 && contacts.length === 0}
            >
              <Download size={18} />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Rechercher par nom, email, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Soumissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.total_submissions || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Estimations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{stats.total_estimations || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Rappels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{stats.total_callbacks || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Contacts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{stats.total_contacts || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="estimations" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="estimations">
              Estimations ({estimations.length})
            </TabsTrigger>
            <TabsTrigger value="callbacks">
              Demandes de rappel ({callbacks.length})
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Estimations Tab */}
          <TabsContent value="estimations" className="space-y-4">
            {filteredEstimations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  Aucune estimation pour le moment
                </CardContent>
              </Card>
            ) : (
              filteredEstimations.map((est) => (
                <Card key={est.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{est.name}</h3>
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                          {getProjectTypeLabel(est.project_type)}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500 flex items-center justify-end gap-1">
                          <Calendar size={14} />
                          {formatDate(est.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={16} />
                        <span className="text-sm">{est.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone size={16} />
                        <span className="text-sm">{est.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin size={16} />
                        <span className="text-sm">{est.postal_code}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <User size={16} />
                        <span className="text-sm">{est.surface} m²</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Euro size={16} />
                        <span className="text-sm">{getBudgetLabel(est.budget)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={16} />
                        <span className="text-sm">{getTimelineLabel(est.timeline)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Callbacks Tab */}
          <TabsContent value="callbacks" className="space-y-4">
            {filteredCallbacks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  Aucune demande de rappel pour le moment
                </CardContent>
              </Card>
            ) : (
              filteredCallbacks.map((cb) => (
                <Card key={cb.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{cb.name}</h3>
                        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                          Rappel demandé
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(cb.created_at)}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone size={16} />
                        <span className="text-sm font-medium">{cb.phone}</span>
                      </div>
                      {cb.message && (
                        <div className="flex items-start gap-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
                          <MessageSquare size={16} className="mt-1 flex-shrink-0" />
                          <span className="text-sm">{cb.message}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            {filteredContacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  Aucun message pour le moment
                </CardContent>
              </Card>
            ) : (
              filteredContacts.map((contact) => (
                <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{contact.name}</h3>
                        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                          Message
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(contact.created_at)}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail size={16} />
                        <span className="text-sm">{contact.email}</span>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} />
                          <span className="text-sm">{contact.phone}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
                        <MessageSquare size={16} className="mt-1 flex-shrink-0" />
                        <span className="text-sm">{contact.message}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
